/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI
 * (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import ColorWheel from './ColorWheel';
import ColorGrid from './ColorGrid';
import ColorConversion from 'utilities/ColorConversion';
import PaletteAlgorithms from 'utilities/PaletteAlgorithms';
import _ from 'underscore';
import StringResources from 'nls/StringResources';
import {
	TextInput,
	Select,
	Tabs,
	TabPanel,
	Dialog,
	RadioGroup,
	Button,
	Label,
	Separator
} from 'ca-ui-toolkit';
import '../../stylesheets/all.scss';
import delSwatch from '@ba-ui-toolkit/ba-graphics/dist/icons-js/delete_16';
import addSwatch from '@ba-ui-toolkit/ba-graphics/dist/icons-js/add--filled_16';
import reversePalette from '@ba-ui-toolkit/ba-graphics/dist/icons-js/repeat_32';

// CustomPalette class
export default class CustomPalette extends Component{

	static propTypes = {
		glassContext: PropTypes.object,
		paletteDef: PropTypes.string,
		paletteId: PropTypes.string,
		onPaletteCreated: PropTypes.func,
		removeDialog: PropTypes.func,
		type: PropTypes.string,
		userClass: PropTypes.string,
		paletteType: PropTypes.string,
		isDialogOpen: PropTypes.bool,
		paletteService: PropTypes.object
	}
	static defaultProps = {
		isDialogOpen: false
	};

	// Constructor
	// State consists of property to define whether or not the dialog is open or closed, the selectedTab for the tab panel and the selected item in the select drop down list
	constructor(props) {
		super(props);

		this.swatchWrapperRef = null;
		this.continousWrapperRef = null;

		this.state = {
			isDialogOpen: props.isDialogOpen || false,
			selectedTab: 'idColorGrid',
			selectedColorType: 'RGB',
			paletteNameValue: '',
			radioGroupSelectedOption: 'auto',
			swatchCount: this.props.paletteType == 'continuous' ? 2 : 10,
			paletteDirection: 'forward',
			paletteType: 'standard',
			currentSelectIndex: 0,
			insufficientColors: false,
			noName: false,
			paletteColors: this.props.paletteType == 'continuous' ? ['#FFFFFF', '#FFFFFF'] : []
		};
	}

	/**
	* componentDidMount - Lifecycle method. Executes before first render. Executes when the element is inserted into the virtual DOM but before the HTML node itself can be manipulated.
	* Use this point to configure the element as required based on properties passed upon creation
	**/
	componentDidMount = () =>{

		//Check to see if a palette definition exists (opening a palette for edit or duplicating)
		//If one exists set state and properties accordingly from the palette definition
		if(this.props.paletteDef){
			let paletteColors, paletteType;
			if ( this.props.paletteDef.fillType === 'continuous' ) {
				paletteType = 'continuous';
				paletteColors = _.map(this.props.paletteDef.fills, (aFill) => {return aFill.fill;});
			} else {
				paletteType = 'standard';
				paletteColors = _.map(this.props.paletteDef.fills, (aFill) => {return aFill.toUpperCase();});
			}

			this.setState({
				paletteColors: paletteColors,
				paletteType: paletteType,
				currentSelectIndex: 0,
				paletteNameValue: this.props.paletteDef.label,
				swatchCount: paletteColors.length
			});
		}
	}

	/**
	*	componentDidUpdate - Lifecycle method. Executes after first render and any time a render is executed. Use this opportunity to manipulate the HTML node as required.
	* @param {Object} prevProps - Object holding the key value pairs of the previous properties used to render this element
	* @param {Object} prevState - Object holding the key value pairs of the previous state used to render this element
	**/
	componentDidUpdate(prevProps, prevState){

		if(this.props.paletteType && (this.state.paletteType != prevProps.paletteType)){
			this.setState({ paletteType: this.props.paletteType });
		}

		if(this.state.paletteType == 'continuous'){
			if(this.swatchWrapperRef){
				this.swatchWrapperRef.style.display = 'none';
			}
			if(this.continousWrapperRef){
				this.continousWrapperRef.style.display = 'block';
			}
			if(this.state.paletteType != prevState.paletteType){
				let colorGuide = this.state.paletteType == 'continuous' ? 'custom' : this.state.radioGroupSelectedOption;
				this.setState({ radioGroupSelectedOption: colorGuide });
			}
		}else if(this.state.paletteType == 'standard'){
			if(this.continousWrapperRef){
				this.continousWrapperRef.style.display = 'none';
			}
			if(this.swatchWrapperRef){
				this.swatchWrapperRef.style.display = 'block';
			}
		}

		if(this.state.swatchCount > 12 && this.swatchWrapperRef){
			this.swatchWrapperRef.style.height = '80px';
		}else if(this.swatchWrapperRef){
			this.swatchWrapperRef.style.height = '40px';
		}
	}

	/**
	* addSwatchEntry - Add a swatch to the end of the dialog
	**/
	addSwatchEntry = () =>{
		if( this.state.paletteType == 'continuous' ){
			if ( this.state.swatchCount > 8 ) {
				return;
			}
			let newPaletteColors = [];
			for ( let i = 0; i < this.state.paletteColors.length; i++ ) {
				newPaletteColors.push(this.state.paletteColors[i]);
			}
			newPaletteColors.push('#FFFFFF');
			this.setState({
				swatchCount: newPaletteColors.length,
				currentSelectIndex: newPaletteColors.length-1,
				paletteColors: newPaletteColors
			});
		} else {
			let count = this.state.swatchCount + 1;
			this.setState({
				swatchCount: count,
				currentSelectIndex: count - 1
			});
		}
	}

	/**
	* removeEntryAtIndex - Remove the color entry in paletteColors at the corresponding index
	**/
	removeSwatchEntry = () =>{
		if( this.state.swatchCount < 3){
			return;
		}
		let newPaletteColors = [];
		for ( let i = 0; i < this.state.paletteColors.length; i++ ) {
			if ( i != this.state.currentSelectIndex ){
				newPaletteColors.push(this.state.paletteColors[i]);
			}
		}

		let newCount = this.state.swatchCount - 1;
		// if it was the last one, move the index, otherwise leave it alone
		let selectedIndex = this.state.currentSelectIndex >= newCount ? newCount -1 : this.state.currentSelectIndex;

		this.setState({
			paletteColors: newPaletteColors,
			currentSelectIndex: selectedIndex,
			swatchCount: newCount
		});
	}

	/**
	* reversePalette - Change the order of the colors in the swatches as well as the corresponding palette array.
	* In the case of continuous palettes change the direction of the gradient preview
	**/
	reversePalette = () =>{
		let newPaletteColors = [];
		if( this.state.paletteType == 'standard'){
			// add any padded empty cells to the front
			for ( let i = 0; i < this.state.swatchCount - this.state.paletteColors.length; i++ ) {
				newPaletteColors.push(null);
			}
		}

		for ( let i = this.state.paletteColors.length-1; i >= 0 ; i-- ) {
			newPaletteColors.push(this.state.paletteColors[i]);
		}
		let newSelectedIndex = this.state.swatchCount - 1 - this.state.currentSelectIndex;
		this.setState({
			paletteColors: newPaletteColors,
			currentSelectIndex: newSelectedIndex
		});
	}

	/**
	* getSwatch - Creates a swatch element at the given index and sets the corresponding className, id, style, swatchindex properties
	* Also sets the swatch element onClick and ref attributes
	* @param {Number} i - Index of the current swatch element being created
	**/
	getSwatch(i){
		let backgroundColor = i < this.state.paletteColors.length ? this.state.paletteColors[i] : '#FFFFFF';
		let hasPalette = (i < this.state.paletteColors.length && this.state.paletteColors[i]);
		let isSelected = i == this.state.currentSelectIndex;
		let className = (isSelected && !hasPalette) ? 'clsSwatchStyle_selected' : isSelected && hasPalette ? 'clsSwatchStyle_filled_selected' : hasPalette ? 'clsSwatchStyle_filled' : 'clsSwatchStyle_empty';
		return (<div className={className} key={'swatch' + i} id={'swatch' + i}  style={{ backgroundColor: backgroundColor }} onClick={this.selectSwatch.bind(this, i)}></div>);
	}

	generateSwatches = () =>{
		const swatches = [];
		for(let i = 0; i < this.state.swatchCount; i++) {
			swatches.push(this.getSwatch(i));
		}
		return swatches;
	}

	selectSwatch(swatchIndex){
		if ( swatchIndex != this.state.currentSelectIndex ) {
			this.setState({ currentSelectIndex: swatchIndex });
		}
	}

	onColorChange( color, isGreyScale ){
		let newPaletteColors = [];
		let selectedIndex = this.state.currentSelectIndex || 0;
		for ( let i = 0; i < this.state.paletteColors.length; i++ ) {
			newPaletteColors.push(this.state.paletteColors[i]);
		}

		if ( this.state.radioGroupSelectedOption == 'custom' ){
			newPaletteColors[selectedIndex] = color;
		} else if ( this.state.radioGroupSelectedOption == 'auto'){
			let hslColor = ColorConversion.convertRgbToHsl( color );
			let swatchCount = this.state.swatchCount - this.state.currentSelectIndex;
			let hslColors = isGreyScale ?
				PaletteAlgorithms.createPaletteViaLight(swatchCount, hslColor, isGreyScale) :
				PaletteAlgorithms.createPaletteViaHue(swatchCount, hslColor, isGreyScale);
			newPaletteColors[selectedIndex] = color;
			// start from 1, leaving the current "seed" color unchanged to avoid roundoff in RGB->HSL->RGB
			for(let i = 1; i < hslColors.length; i++){
				let rgbVal = ColorConversion.convertHslToRgb(hslColors[i]);
				let rgbHex = ColorConversion.convertRgbValToRgbHex(rgbVal);
				newPaletteColors[selectedIndex + i] = rgbHex;
			}
		}
		// we always set insufficient colors to false (even if there is not enough) on a color change to keep the error indicator from being noisy
		this.setState({
			paletteColors: newPaletteColors,
			insufficientColors: false
		});
	}

	removeEmptySwatches(currentSwatch){
		return !!currentSwatch;
	}

	deriveContinuousFills(){
		let fillsCopy = this.state.paletteColors.filter(this.removeEmptySwatches);
		// this only supports 2 colors
		if ( fillsCopy.length < 2 ) {
			return [];
		}
		let contFills = [];
		let step = 1/(fillsCopy.length-1);
		for(let i = 0; i < fillsCopy.length; i++){
			let currFill = {
				fill: fillsCopy[i],
				at: (step * i)
			};
			contFills.push(currFill);
		}
		return contFills;
	}

	applyPaletteColor = () =>{
		let namePresent = !!this.state.paletteNameValue;
		let sufficientColors = false;

		let paletteFills = null;
		let currFillType = null;
		if(this.state.paletteType == 'continuous'){
			paletteFills = this.deriveContinuousFills();
			sufficientColors = paletteFills.length > 1;
			currFillType = 'continuous';
		}else{
			paletteFills = this.state.paletteColors.filter(this.removeEmptySwatches);
			currFillType = 'simple';
			sufficientColors = paletteFills.length > 1;
		}
		if ( !namePresent || !sufficientColors ) {
			this.setState( {
				noName: !namePresent,
				insufficientColors: !sufficientColors
			});
			return;
		}

		var palette = { label: this.state.paletteNameValue, fillType: currFillType, fills: paletteFills };
		const isPublic = this.props.type == 'global';
		const paletteServicePromise = this.props.paletteService? Promise.resolve(this.props.paletteService) : this.props.glassContext.getSvc('.Palette');

		if(this.props.paletteDef && this.props.paletteId) {
			const options = { id: this.props.paletteId, palette: palette };
			paletteServicePromise.then((service) => {
				service.updatePalette(options).then(() => {
					this.closeDialog();
				}, (error) => {
					console.log(error);
				});
			});
		} else if(!isPublic){
			paletteServicePromise.then((service) => {
				service.createPalette({ palette: palette }).then((paletteId) => {
					if (this.props.onPaletteCreated){
						this.props.onPaletteCreated(paletteId);
					}
					this.closeDialog();
				}, (error) => {
					console.log(error);
				});
			});
		} else if(isPublic){
			paletteServicePromise.then((service) => {
				service.createPalette({ palette: palette, public: isPublic }).then((paletteId) => {
					if (this.props.onPaletteCreated){
						this.props.onPaletteCreated(paletteId);
					}
					this.closeDialog();
				}, (error) => {
					console.log(error);
				});
			});
		}
	}

	openDialog = () => {
		this.setState({ isDialogOpen: true });
	}

	closeDialog = () => {
		this.setState({ isDialogOpen: false });
		if(this.props.removeDialog){
			this.props.removeDialog();
		}
	}

	changePaletteType(val){
		if( this.state.paletteType == val ) {
			return;
		}

		this.setState({
			paletteType: val,
			paletteColors: val == 'standard' ? [] : ['#FFFFFF', '#FFFFFF'],
			currentSelectIndex: 0,
			swatchCount: val == 'standard' ? 10 : 2
		});
	}

	getContinuousPreview(){
		let fillsCopy = this.state.paletteColors.filter(this.removeEmptySwatches);
		let gradientColor = '#FFFFFF';
		if ( fillsCopy.length > 1 ) {
			gradientColor = 'linear-gradient(to right, ' + fillsCopy.join(',') + ')';
		}
		let fillSelectors = [];
		if ( this.state.swatchCount > 1){
			for ( let i = 0; i < this.state.swatchCount; i++ ) {
				let className = i == this.state.currentSelectIndex ? 'clsSelector' : 'clsContinuousIndicator';
				fillSelectors.push(
					<div key={'selector_' + i} className={'clsContinuousIndicatorContainer'} >
						<div className={className} style={{ backgroundColor: this.state.paletteColors[i] }} onClick={this.selectSwatch.bind(this, i)}></div>
					</div>
				);
			}
		}
		let roundedEdgeClass = '';
		if ( this.state.currentSelectIndex == 0 ) {
			roundedEdgeClass = ' clsContinuousPreviewRoundLeft';
		} else if ( this.state.swatchCount == this.state.currentSelectIndex + 1 ) {
			roundedEdgeClass = ' clsContinuousPreviewRoundRight' ;
		}
		return <div style={{ background: gradientColor }} id='idContinuousPreview' className={'clsContinuousPreview' + roundedEdgeClass}>{fillSelectors}</div>;
	}

	getContinuousButtons(){
		return(
			<tbody>
				<tr>
					<td>
						<div className={this.state.paletteType == 'standard' ? 'clsPaletteIcons_wrapper_selected' : 'clsPaletteIcons_wrapper'} >
							<div
								className='clsPaletteStandard'
								id='imgStandardPalette'
								value={'standard'}
								onClick={() => this.changePaletteType('standard')}
								title={StringResources.get('standardPalette')}
							/>
						</div>
					</td>
					<td>
						<div className={this.state.paletteType == 'continuous' ? 'clsPaletteIcons_wrapper_selected' : 'clsPaletteIcons_wrapper'} >
							<div
								className='clsPaletteGradient'
								id='imgContinuousPalette'
								value={'continuous'}
								onClick={() => this.changePaletteType('continuous')}
								title={StringResources.get('continuousPalette')}
							/>
						</div>
					</td>
				</tr>
			</tbody>
		);
	}

	navigateSwatch(e){
		let currentIndex = this.state.currentSelectIndex;
		var key = e.keyCode;
		switch ( key ){
			case 37:
				//	left arrow
				if ( currentIndex > 0 )
					this.setState({ currentSelectIndex: currentIndex - 1 });
				break;
			case 39:
				// right arrow
				if ( currentIndex != this.state.swatchCount - 1 )
					this.setState({ currentSelectIndex: currentIndex + 1 });
				break;
		}
	}

	navigatePaletteType(e){
		var key = e.keyCode;
		switch ( key ){
			case 37:
				//	left arrow
				if ( this.state.paletteType == 'continuous' )
					this.changePaletteType('standard');
				break;
			case 39:
				// right arrow
				if ( this.state.paletteType == 'standard' )
					this.changePaletteType('continuous');
				break;
		}
	}


	// Function used to render the CustomPalette element (dialog)
	render(){
		const swatch = this.generateSwatches();

		let options = [];
		if(this.state.paletteType == 'continuous'){
			options = [{ label: StringResources.get('modeAutomatic'), value: 'auto', disabled: true }, { label: StringResources.get('modeCustom'), value: 'custom', checked: true }];
		}else{
			options = [{ label: StringResources.get('modeAutomatic'), value: 'auto' }, { label: StringResources.get('modeCustom'), value: 'custom', checked: true }];
		}
		const myList = [{ value: 'RGB', label: StringResources.get('modelRGB') }, { value: 'HSB', label: StringResources.get('modelHSB') }];
		const continuousStandardBtns = (this.props.userClass && this.props.userClass == 'admin') ? this.getContinuousButtons(): null;

		let continuousPreview = null;
		if(this.state.paletteType == 'continuous'){
			continuousPreview = this.getContinuousPreview();
		}

		let dialogTitle = (this.state.paletteType == 'continuous') ? StringResources.get('createContinuousPalette') : StringResources.get('createStandardPalette');
		let colorWarningText = this.state.insufficientColors ? StringResources.get('errorInsufficientColors') : '';

		return(
			(this.state.isDialogOpen &&
			(
				<div>
					<Dialog width='725px' onClose={ this.closeDialog } startingFocusIndex={0}>
						<Dialog.Header>{dialogTitle}</Dialog.Header>
						<Dialog.Body>
							<table style={{ width: '100%', height: '82px' }}>
								<tbody>
									<tr>
										<td style={{ width: '75%', verticalAlign: 'top' }}>
											<Label label={StringResources.get('paletteName')} id='idNameLabel' htmlFor='idPaletteName' type='caption'/>
											<TextInput
												placeholder={StringResources.get('paletteNamePlaceholder')}
												id='idPaletteName'
												value={this.state.paletteNameValue}
												onChange={value => this.setState({ paletteNameValue: value, noName: !value })}
												style={{ width: '100%' }}
												label={StringResources.get('paletteName')}
												autoFocus
												validationErrorText={StringResources.get('errorNoName')}
												hasValidationError={this.state.noName}
											/>
										</td>
										<td style={{ float: 'right' }}>
											<table style={{ marginTop: '15px' }} tabIndex={(continuousStandardBtns ? '0' : '-1')} onKeyDown={this.navigatePaletteType.bind(this)}>
												{continuousStandardBtns}
											</table>
										</td>
									</tr>
								</tbody>
							</table>

							<div className='clsPaletteContainer' style={{ margin: '6px 0px 6px' }}>
								<table style={{ width: '100%' }}>
									<tbody>
										<tr>
											<td style={{ width: '90%' }}>
												<div style={{ height: '40px', width: '95%', overflow:'auto' }} ref={(element) => {this.swatchWrapperRef = element;}} tabIndex='0' onKeyDown={this.navigateSwatch.bind(this)}>
													{swatch}
												</div>
												<div style={{ height: '40px', width: '95%', overflow:'auto', display:'none' }} ref={(element) => {this.continousWrapperRef = element;}} tabIndex='0' onKeyDown={this.navigateSwatch.bind(this)}>
													{continuousPreview}
												</div>
											</td>
											<td>
												<div id='idDelSwatchDiv' style={{ float: 'right' }}>
													<Button
														id='idDelSwatch'
														intent='primary'
														icon={delSwatch.id}
														iconSize='small'
														variant='icon'
														onClick={this.removeSwatchEntry}
														title={StringResources.get('removeSwatch')}
														tabIndex={0}
													/>
												</div>
											</td>
											<td>
												<Separator
													orientation='vertical'
													style={{ height: '20px', color: '#C0BFC0' }}
												/>
											</td>
											<td>
												<div id='idAddSwatchDiv' style={{ float: 'left' }}>
													<Button
														id='idAddSwatch'
														intent='primary'
														icon={addSwatch.id}
														iconSize='small'
														variant='icon'
														onClick={this.addSwatchEntry}
														title={StringResources.get('addSwatch')}
														tabIndex={0}
													/>
												</div>
											</td>
											<td style={{ paddingRight: '10px' }}>
												<div id='idReverse' style={{ float: 'left' }}>
													<Button
														id='idReverse'
														intent='primary'
														icon={reversePalette.id}
														iconSize='small'
														variant='icon'
														onClick={this.reversePalette.bind(this)}
														title={StringResources.get('reversePalette')}
														tabIndex={0}
													/>
												</div>
											</td>
										</tr>
									</tbody>
								</table>
								<span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#e62325', wordBreak: 'break-word', wordWrap: 'break-word' }}>
									{ colorWarningText }&nbsp;
								</span>
							</div>
							<div>
								<Tabs selected={this.state.selectedTab} onChange={tabId => this.setState({ selectedTab: tabId }) }>
									<TabPanel keepTabContent={true} id='idColorGrid' label={StringResources.get('grid')}>
										<div style={{ height:'275px', paddingTop: '24px' }}>
											<table>
												<tbody>
													<tr>
														<td style={{ width: '10%', verticalAlign: 'top' }}>
															<Label label={StringResources.get('colorGuide')} id='idRadioGridLabel' htmlFor='idGridRadioGroup' type='caption'/>
															<RadioGroup
																id='idGridRadioGroup'
																direction='vertical'
																options={options}
																onChange={val => this.setState({ radioGroupSelectedOption: val })}
																checked={this.state.radioGroupSelectedOption}
																tabIndex={0}
																aria-labelledby='idRadioGridLabel'
															/>
														</td>
														<td style={{ width: '90%', paddingLeft: '24px' }}>
															<ColorGrid
																id='idColorGridComponent'
																onColorChange={this.onColorChange.bind(this)}
																color={this.state.paletteColors[this.state.currentSelectIndex]}
															/>
														</td>
													</tr>
												</tbody>
											</table>
										</div>
									</TabPanel>
									<TabPanel keepTabContent={true} id='idColorWheel' label={StringResources.get('wheel')}>
										<div style={{ height:'275px', paddingTop: '24px' }}>
											<table>
												<tbody>
													<tr>
														<td style={{ width: '10%', verticalAlign: 'top' }}>
															<table>
																<tbody>
																	<tr>
																		<td>
																			<Label label={StringResources.get('colorModel')} id='idSelectGroupLabel' htmlFor='idSelectGroup' type='caption'/>
																			<Select
																				id='idSelectGroup'
																				selected={this.state.selectedColorType}
																				fixedWidth
																				options={myList}
																				onChange={(value) => this.setState({ selectedColorType: value })}
																				customWidth='100px'
																				variant='frame'
																			/>
																		</td>
																	</tr>
																	<tr>
																		<td style={{ paddingTop: '10px' }}>
																			<Label label={StringResources.get('colorGuide')} id='idRadioWheelLabel' htmlFor='idWheelRadioGroup' type='caption'/>
																			<RadioGroup
																				id='idWheelRadioGroup'
																				direction='vertical'
																				options={options}
																				onChange={val => this.setState({ radioGroupSelectedOption: val })}
																				checked={this.state.radioGroupSelectedOption}
																				aria-labelledby='idRadioWheelLabel'
																			/>
																		</td>
																	</tr>
																</tbody>
															</table>
														</td>
														<td style={{ width: '90%', paddingLeft: '24px' }}>
															<ColorWheel
																id='idColorWheelComponent'
																onColorChange={this.onColorChange.bind(this)}
																selectedColorType={this.state.selectedColorType}
																color={this.state.paletteColors[this.state.currentSelectIndex]}
															/>
														</td>
													</tr>
												</tbody>
											</table>
										</div>
									</TabPanel>
								</Tabs>
							</div>
						</Dialog.Body>
						<Dialog.Footer>
							<Dialog.Button id='idSaveBtn' label={StringResources.get('saveDialog')} onClick={ this.applyPaletteColor } tabIndex={0} intent='primary' variant='solid'/>
							<Dialog.Button id='idCancelBtn' label={StringResources.get('cancelDialog')} onClick={ this.closeDialog } tabIndex={0} intent='primary' variant='frame'/>
						</Dialog.Footer>
					</Dialog>
				</div>
			)
			) || null
		);
	}
}

CustomPalette.defaultProps = {
	name: 'CustomPaletteDialog'
};



// WEBPACK FOOTER //
// ./src/js/dialog/CustomPalette.js